import { createAsyncThunk, Dispatch } from '@reduxjs/toolkit';
import type { User } from '@stash/shared-types';
import type { RootState } from '../reducers/index';
import { auth } from '../services/firebase';
import AuthService from '../services/auth-service';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { loginUser, loginFailed, authStateChanged, setLoading } from '../reducers/auth-reducer';
import log from '@/services/log';

export interface AuthInfo {
  username: string;
  password: string;
}

interface FirebaseError {
  code?: string;
  message?: string;
}

export const loginAsync = createAsyncThunk<User, AuthInfo, { rejectValue: string }>(
  'auth/loginAsync',
  async (authInfo, { rejectWithValue }) => {
    try {
      const authService = new AuthService();
      const response = await authService.login(authInfo);
      return response.user as User;
    } catch (err) {
      const error = err as FirebaseError;
      log.error('Login failed:', error);

      let errorMessage = 'Login failed';

      if (error.code) {
        switch (error.code) {
          case 'auth/user-not-found':
          case 'auth/wrong-password':
          case 'auth/invalid-credential':
            errorMessage = 'Invalid credentials';
            break;
          case 'auth/invalid-email':
            errorMessage = 'Invalid email address';
            break;
          case 'auth/user-disabled':
            errorMessage = 'Account has been disabled';
            break;
          case 'auth/too-many-requests':
            errorMessage = 'Too many failed attempts. Please try again later';
            break;
          case 'auth/network-request-failed':
            errorMessage = 'Network error. Please check your connection';
            break;
          default:
            errorMessage = error.message || 'An unknown error occurred';
        }
      }

      return rejectWithValue(errorMessage);
    }
  },
);

export const logoutAsync = createAsyncThunk<null, void, { state: RootState; rejectValue: string }>(
  'auth/logoutAsync',
  async (_, { getState, rejectWithValue }) => {
    try {
      const user = getState().auth.currentUser;
      const authService = new AuthService();
      await authService.logout(user);
      return null;
    } catch (err) {
      const error = err as FirebaseError;
      return rejectWithValue(error.message || 'Logout failed');
    }
  },
);

export const initializeAuthListener = () => {
  return (dispatch: Dispatch): void => {
    onAuthStateChanged(auth, (user: FirebaseUser | null) => {
      if (user) {
        const userInfo: User = {
          userId: user.uid,
          name: user.displayName || user.email || 'Stasher',
          email: user.email ?? '',
          ...(user.photoURL ? { avatar: user.photoURL } : {}),
        };
        dispatch(authStateChanged(userInfo));
      } else {
        dispatch(authStateChanged(null));
      }
    });
  };
};

export const login = (authInfo: AuthInfo): ReturnType<typeof loginAsync> => loginAsync(authInfo);
export const logout = (): ReturnType<typeof logoutAsync> => logoutAsync();

export { loginUser, loginFailed, authStateChanged, setLoading as setAuthLoading };
